import { useState, useEffect } from 'react';
import {
  collection,
  query,
  orderBy,
  onSnapshot,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  Timestamp,
} from 'firebase/firestore';
import { db, app } from '../../../utils/init-firebase';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { CRMIdea, CRM_IDEAS_COLLECTION, Screenshot } from './types';

/**
 * Hook for CRM Ideas/Feedback data with Firestore
 * Manages ideas and fixes tracking for CRM users
 */
export function useCRMIdeas(userId: string | undefined) {
  const [ideas, setIdeas] = useState<CRMIdea[]>([]);
  const [loading, setLoading] = useState(true);

  // Load ideas with real-time listener
  useEffect(() => {
    if (!userId) {
      setLoading(false);
      return;
    }

    const ideasQuery = query(
      collection(db, CRM_IDEAS_COLLECTION),
      orderBy('createdAt', 'desc')
    );

    const unsubscribe = onSnapshot(
      ideasQuery,
      (snapshot) => {
        const ideasData = snapshot.docs.map((docSnap) => {
          const data = docSnap.data();
          return {
            id: docSnap.id,
            ...data,
            createdAt: data.createdAt || Timestamp.now(),
            completedAt: data.completedAt || null,
          } as CRMIdea;
        });
        setIdeas(ideasData);
        setLoading(false);
      },
      (error) => {
        console.error('Error loading CRM ideas:', error);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [userId]);

  // Create idea with email notification
  const createIdea = async (formData: {
    title: string;
    description: string;
    type: 'idea' | 'fix';
    screenshots?: Screenshot[];
    userName: string;
    userEmail: string;
  }) => {
    if (!userId) return;

    try {
      const ideaData = {
        title: formData.title,
        description: formData.description,
        type: formData.type,
        screenshots: formData.screenshots || [],
        status: 'new' as const,
        createdBy: userId,
        createdByName: formData.userName,
        createdByEmail: formData.userEmail,
        createdAt: Timestamp.now(),
      };

      // Save to Firestore
      const docRef = await addDoc(collection(db, CRM_IDEAS_COLLECTION), ideaData);

      // Send email notification
      await sendEmailNotification({
        ...ideaData,
        id: docRef.id,
      });

      return docRef.id;
    } catch (error) {
      console.error('Error creating CRM idea:', error);
      throw error;
    }
  };

  // Send email notification for new ideas
  const sendEmailNotification = async (idea: {
    id: string;
    title: string;
    description: string;
    type: 'idea' | 'fix';
    createdByName: string;
    createdByEmail: string;
    screenshots?: Screenshot[];
  }) => {
    try {
      const functions = getFunctions(app, 'us-east1');
      const sendEmail = httpsCallable(functions, 'sendEmailWithSignature');

      // Build screenshot links HTML
      let screenshotHtml = '';
      if (idea.screenshots && idea.screenshots.length > 0) {
        screenshotHtml = `
          <br/><br/>
          <strong>Attached Screenshots:</strong><br/>
          ${idea.screenshots.map((s, i) => `<a href="${s.url}" target="_blank">Screenshot ${i + 1}: ${s.name}</a>`).join('<br/>')}
        `;
      }

      const typeLabel = idea.type === 'idea' ? '💡 New Idea' : '🔧 Bug Fix Request';

      await sendEmail({
        to: 'feedback@future.loans',
        subject: `[CRM ${idea.type === 'idea' ? 'Idea' : 'Fix'}] ${idea.title}`,
        body: `
          <h2>${typeLabel}</h2>
          <p><strong>From:</strong> ${idea.createdByName} (${idea.createdByEmail})</p>
          <p><strong>Title:</strong> ${idea.title}</p>
          <p><strong>Description:</strong></p>
          <p>${idea.description.replace(/\n/g, '<br/>')}</p>
          ${screenshotHtml}
          <br/><hr/>
          <p><em>Submitted via CRM Feedback System</em></p>
          <p><a href="https://fhl.one/crm/ideas">View in CRM Ideas Dashboard</a></p>
        `,
        fromEmail: idea.createdByEmail,
      });
    } catch (error) {
      console.error('Error sending CRM idea notification email:', error);
      // Don't throw - idea was saved, email failure shouldn't break the flow
    }
  };

  // Update idea (mark as completed)
  const updateIdea = async (ideaId: string, updates: Partial<CRMIdea>, completedByName?: string) => {
    try {
      const ideaRef = doc(db, CRM_IDEAS_COLLECTION, ideaId);
      const updateData: Record<string, unknown> = {
        ...updates,
      };

      // If marking as completed, set completedAt and completedBy
      if (updates.status === 'completed' && userId) {
        updateData.completedAt = Timestamp.now();
        updateData.completedBy = userId;
        if (completedByName) {
          updateData.completedByName = completedByName;
        }
      }

      await updateDoc(ideaRef, updateData);
    } catch (error) {
      console.error('Error updating CRM idea:', error);
      throw error;
    }
  };

  // Delete idea (admin only - check in component)
  const deleteIdea = async (ideaId: string) => {
    try {
      const ideaRef = doc(db, CRM_IDEAS_COLLECTION, ideaId);
      await deleteDoc(ideaRef);
    } catch (error) {
      console.error('Error deleting CRM idea:', error);
      throw error;
    }
  };

  return {
    ideas,
    loading,
    createIdea,
    updateIdea,
    deleteIdea,
  };
}



